import { useEffect, useRef } from 'react';
import { useSettings } from '../Settings/SettingsProvider';
import { CambridgePixelRadarLayer } from './CambridgePixelRadarLayer';
import { useRadarSettings } from './useRadarSettings';

/**
 * Renders CambridgePixelRadarLayer for each enabled radar source in settings.
 * Mount this once in the Map component tree.
 */
export function RadarOverlayLayers() {
  const { settings } = useSettings();
  const { setRadarEnabled } = useRadarSettings();
  const radarSources = settings?.radarSettings ?? [];
  const layerOrdering =
    settings?.['ribbonPlugin.settings']?.layer_ordering?.layers ?? [];

  // `setRadarEnabled` closes over settings data that is a fresh array/object
  // reference every render, so it can't be made referentially stable with
  // useCallback. Keep a ref to the latest version instead (the "latest ref"
  // pattern) so the effect below can subscribe to the window event ONCE and
  // still always invoke the current callback.
  const setRadarEnabledRef = useRef(setRadarEnabled);
  useEffect(() => {
    setRadarEnabledRef.current = setRadarEnabled;
  });

  // Sync layer control removal back to settings (same pattern as Coverage).
  // Subscribes once on mount; re-subscribing every render would be wasteful
  // churn for a window-level listener.
  useEffect(() => {
    const handleRemoveRadar = (e: Event) => {
      const { radarId } = (e as CustomEvent).detail;
      if (radarId) setRadarEnabledRef.current(radarId, false);
    };

    window.addEventListener('removeRadar', handleRemoveRadar);
    return () => {
      window.removeEventListener('removeRadar', handleRemoveRadar);
    };
  }, []);

  return (
    <>
      {radarSources
        .filter((r) => r.enabled)
        .map((r) => {
          const orderingEntry = layerOrdering.find(
            (l: any) => l.type === 'cambridge-radar' && l.id === r.id,
          );
          const initialHidden = orderingEntry ? !orderingEntry.enabled : false;
          return (
            <CambridgePixelRadarLayer
              key={r.id}
              radarInfo={r}
              initialHidden={initialHidden}
            />
          );
        })}
    </>
  );
}
